import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../App';
import { CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionSuccess = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      toast.error('No session ID found');
      navigate('/pricing');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, currentAttempt = 0) => {
    if (currentAttempt >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please check your dashboard.');
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data
        const userResponse = await api.get('/auth/me');
        setUser(userResponse.data);
        toast.success('Subscription activated successfully!');
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
        return;
      }

      // Continue polling
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(sessionId, currentAttempt + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Failed to verify payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-ledger-bone flex items-center justify-center relative noise-overlay">
      <div className="relative z-10 max-w-md w-full mx-4">
        <div className="border-2 border-black/10 bg-white p-12 text-center">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-ledger-green mx-auto mb-4 animate-spin" />
              <h1 className="font-serif text-2xl font-bold text-ledger-green mb-2" data-testid="checking-title">Verifying Payment</h1>
              <p className="font-mono text-sm text-gray-600 mb-4">
                Please wait while we confirm your subscription...
              </p>
              <p className="font-mono text-xs text-gray-500">
                Attempt {attempts + 1} of {maxAttempts}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-bold text-ledger-green mb-2" data-testid="success-title">Subscription Activated!</h1>
              <p className="font-mono text-sm text-gray-600 mb-4">
                Your subscription is now active. Redirecting to dashboard...
              </p>
            </>
          )}

          {status === 'timeout' && (
            <>
              <h1 className="font-serif text-2xl font-bold text-ledger-green mb-2">Verification Timeout</h1>
              <p className="font-mono text-sm text-gray-600 mb-6">
                We're still processing your payment. Please check your dashboard in a few minutes.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-ledger-green text-white rounded-none hover:bg-ledger-green/90 uppercase tracking-widest text-xs py-3 px-6 font-mono"
                data-testid="goto-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="font-serif text-2xl font-bold text-ledger-green mb-2">Verification Error</h1>
              <p className="font-mono text-sm text-gray-600 mb-6">
                We encountered an error verifying your payment. Please contact support.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-ledger-green text-white rounded-none hover:bg-ledger-green/90 uppercase tracking-widest text-xs py-3 px-6 font-mono"
                data-testid="goto-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;